/**
 * "Español del profe" — la VARIEDAD de español con la que speak arma el
 * andamiaje (explicaciones, ayudas, tips de corrección, glosas, el desafío del
 * juego de traducción). NO toca el inglés que se practica (eso es
 * `conversationPrefs.region`: us/british/australian, otro eje).
 *
 * Contrato de resolución (decisión del dueño, 2026-09-24), en este orden:
 *   1. pref explícita del user (`conversationPrefs.spanishVariant`);
 *   2. su país de origen (`user_profiles.origin_country`, ISO alpha-2):
 *      AR/UY → `ar`, MX → `mx`, CO → `co`, ES → `es`, CL → `cl`, otro → `neutral`;
 *   3. sin país → `ar` ({@link LEGACY_SPANISH_VARIANT}): hasta hoy TODOS los
 *      users recibían rioplatense; un user sin país declarado sigue igual, así
 *      nada le cambia en silencio. Es parte del contrato, no un fallback.
 *
 * Se resuelve UNA vez en el borde (arranque de sesión / request REST); de ahí
 * para adentro la variedad viaja resuelta y un valor faltante TIRA.
 */

/** Las seis variedades. `neutral` = neutro latinoamericano (tú, sin modismos). */
export const SPANISH_VARIANTS = ['ar', 'mx', 'co', 'es', 'cl', 'neutral'] as const;
export type SpanishVariant = (typeof SPANISH_VARIANTS)[number];

/** Variedad de un user sin pref ni país: la histórica (todos eran rioplatense). */
export const LEGACY_SPANISH_VARIANT: SpanishVariant = 'ar';

/** País → variedad. Los países fuera de la tabla van a `neutral`. */
const COUNTRY_VARIANT: Readonly<Record<string, SpanishVariant>> = {
  AR: 'ar',
  UY: 'ar',
  MX: 'mx',
  CO: 'co',
  ES: 'es',
  CL: 'cl',
};

export function isSpanishVariant(value: unknown): value is SpanishVariant {
  return typeof value === 'string' && (SPANISH_VARIANTS as readonly string[]).includes(value);
}

/**
 * Variedad por país de origen. El código tiene que ser ISO alpha-2 (el borde
 * `me-profile` ya lo valida): cualquier otra cosa tira — no adivinamos.
 */
export function spanishVariantForCountry(code: string): SpanishVariant {
  const norm = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(norm)) {
    throw new Error(`spanishVariantForCountry: origin_country inválido (${JSON.stringify(code)})`);
  }
  return COUNTRY_VARIANT[norm] ?? 'neutral';
}

/**
 * Resolución completa: pref explícita > país de origen > legacy `ar`. Una pref
 * con un valor fuera del enum tira (vendría de un bug o de un dato corrupto).
 */
export function resolveSpanishVariant(
  pref: SpanishVariant | null,
  originCountry: string | null,
): SpanishVariant {
  if (pref !== null) {
    if (!isSpanishVariant(pref)) {
      throw new Error(`resolveSpanishVariant: spanishVariant inválido (${String(pref)})`);
    }
    return pref;
  }
  if (originCountry !== null && originCountry.trim().length > 0) {
    return spanishVariantForCountry(originCountry);
  }
  return LEGACY_SPANISH_VARIANT;
}
